/**
 * useMarketStatus - Real-time market status via WebSocket
 */
import { useState, useEffect, useCallback } from 'react'
import { getWebSocketManager, MarketStatus, MarketEvent } from '../lib/websocket'

export interface LiveMarketStatus {
  isOpen: boolean
  regime: string
  spyPrice: number
  spyChange: number
  vix: number
  vixChange: number
  lastUpdate: string
}

interface UseMarketStatusResult extends LiveMarketStatus {
  isConnected: boolean
  lastEvent: MarketEvent | null
}

const DEFAULT_STATUS: LiveMarketStatus = {
  isOpen: false,
  regime: 'neutral',
  spyPrice: 0,
  spyChange: 0,
  vix: 0,
  vixChange: 0,
  lastUpdate: ''
}

export function useMarketStatus(): UseMarketStatusResult {
  const [status, setStatus] = useState<LiveMarketStatus>(DEFAULT_STATUS)
  const [isConnected, setIsConnected] = useState(false)
  const [lastEvent, setLastEvent] = useState<MarketEvent | null>(null)

  // Handle incoming market status or event
  const handleMessage = useCallback((data: MarketStatus | MarketEvent) => {
    if (data.type === 'market_status') {
      const update = data as MarketStatus
      setStatus({
        isOpen: update.is_open,
        regime: update.regime,
        spyPrice: update.spy_price,
        spyChange: update.spy_change_pct,
        vix: update.vix,
        vixChange: update.vix_change_pct,
        lastUpdate: update.timestamp
      })
    } else if (data.type === 'market_open' || data.type === 'market_close') {
      const event = data as MarketEvent
      setLastEvent(event)
      setStatus(prev => ({
        ...prev,
        isOpen: data.type === 'market_open',
        lastUpdate: event.timestamp
      }))
    }
  }, [])

  // Connect on mount - market channel stays connected for app lifetime
  useEffect(() => {
    const wsManager = getWebSocketManager()

    // Track connection status
    const unsubscribeStatus = wsManager.onStatusChange((channel, status) => {
      if (channel === 'market') {
        setIsConnected(status === 'connected')
      }
    })

    // Connect to market channel if not already connected
    if (wsManager.getStatus('market') !== 'connected' && wsManager.getStatus('market') !== 'connecting') {
      wsManager.connectMarket(handleMessage)
    }

    // DO NOT disconnect market on unmount - it should persist for app lifetime
    return () => {
      unsubscribeStatus()
      // Intentionally not disconnecting market channel
    }
  }, [handleMessage])

  return {
    ...status,
    isConnected,
    lastEvent
  }
}

export default useMarketStatus
